import { TAccessReason } from '@/types/general/TAccess.ts';
import { IIdNameBase } from '../general/IIdName.ts';
import { IPublicPages } from './IPublicPages.ts';
import { IEnactAccessRequirement } from './IEnactAccessRequirement.ts';
import { IRichTextPendingImage } from './IRichTextImage.ts';

export interface IDocumentFilter {
    filterCategory: string;
    filters: Array<string>;
}

export interface IDocumentFilterNew {
    filterCategory: IIdNameBase;
    filters: Array<IDocumentFilterNew>;
}

export interface IDocumentFilterOrderResponse {
    filterOrder: Array<IDocumentFilterNew>;
}

export type ReportType = 'Pdf' | 'PowerBi' | 'Excel' | 'Podcast' | 'Webinar' | 'Definition' | 'Feature' | 'Post' | 'LinkedIn';
export type ThreadReportType = 'Feature' | 'Post' | 'LinkedIn';
export type SparkContentReportType = 'Podcast' | 'Webinar' | 'Definition' | ThreadReportType;
export type AdminContentType = 'PDF' | 'PowerBI' | 'Excel' | 'Podcast' | 'Webinar' | 'Definition' | ThreadReportType;
export type ReportTypeFilter = 'Report' | 'Whitepaper' | 'Data';

export type TReportTypeLogo =
    | {
        kind: 'image';
        source: string;
        alt: string;
    }
    | {
        kind: 'icon';
        source: string;
        iconFamily: 'fas' | 'fab';
        alt: string;
        colour?: string;
    };

export interface IReportTypeMetadata {
    reportType: ReportType;
    label: string;
    viewerKind: 'pdf' | 'powerBi' | 'excel' | 'sparkContent';
    browseCategory: 'research' | 'thread' | 'definition';
    logo?: TReportTypeLogo;
    fallbackThumbnail: string;
    thumbnailObjectPosition?: string;
}

export interface IDocumentSimple {
    documentShortCode: string;
    documentTitle: string;
    documentSubtitle?: string;
    hasAccess: boolean;
    publicationDate: string;
    filters: Array<IDocumentFilterNew>;
    description?: string;
    questions: Array<string>;
    reportType: ReportType;
    accessReason: TAccessReason;
    reportTypeFilter: Array<ReportTypeFilter>;
    thumbnailUrl: string;
    parentHeader: string | null;
    myResearch: boolean;
    externalUrl?: string | null;
    spotifyUrl?: string | null;
    updatedAt?: string | null;
    created?: string | null;
    commentCount: number;
    routeNames?: Array<string>;
    isRelevantToUser: boolean;
    siteAreaRelevance: Array<string>;
}

export interface IReportsWithHeader {
    header: string;
    reports: Array<IDocumentSimple>;
    myResearch: boolean;
}

export interface IPdfDetails {
    authors: Array<string>;
    documentShortCode: string;
    documentTitle: string;
    documentSubtitle?: string;
    hasAccess: boolean;
    publicationDate: string;
    filters: Array<IDocumentFilterNew>;
    description?: string;
    questions: Array<string>;
    relatedDocuments: Array<string>;
    transposeId: null | string;
    accessReason: TAccessReason;
    numberOfPages: number;
    commentCount: number;
}

export interface IPowerBiDetails {
    authors: Array<string>;
    documentShortCode: string;
    documentTitle: string;
    documentSubtitle?: string;
    hasAccess: boolean;
    publicationDate: string;
    filters: Array<IDocumentFilterNew>;
    description?: string;
    accessReason: TAccessReason;
    commentCount: number;
}

export interface IExcelDetails {
    authors: Array<string>;
    documentShortCode: string;
    documentTitle: string;
    documentSubtitle?: string;
    hasAccess: boolean;
    publicationDate: string;
    filters: Array<IDocumentFilterNew>;
    description?: string;
    accessReason: TAccessReason;
    fileType: string;
    commentCount: number;
}

export interface ISparkContentDetails {
    authors: Array<string>;
    documentShortCode: string;
    documentTitle: string;
    documentSubtitle?: string;
    hasAccess: boolean;
    publicationDate: string;
    filters: Array<IDocumentFilterNew>;
    description?: string;
    accessReason: TAccessReason;
    reportType: SparkContentReportType;
    bodyHtml: string;
    thumbnailUrl: string;
    fullImageUrl: string;
    externalUrl?: string | null;
    spotifyUrl?: string | null;
    linkedinUrl?: string | null;
    linkedinEmbedUrl?: string | null;
    updatedAt?: string | null;
    commentCount: number;
    routeNames: Array<string>;
    siteAreaRelevance: Array<string>;
}

export const emptyAccess: IAccess = {
    access: [],
};

export const emptyAdminPDFDocument: IAdminPDFDocument = {
    access: undefined,
    publicationDate: new Date().toISOString(),
    fileName: '',
    documentTitle: '',
    documentShortCode: '',
    authors: [],
    filters: [],
    description: '',
    publicPages: undefined,
    created: null,
    updatedAt: '',
};

export const emptyAdminPowerBi: IAdminPowerBi = {
    access: undefined,
    publicationDate: new Date().toISOString(),
    documentTitle: '',
    documentShortCode: '',
    authors: [],
    filters: [],
    powerBiId: '',
    description: '',
    created: null,
    updatedAt: '',
};

export const emptyAdminExcelDocument: IAdminExcelDocument = {
    access: undefined,
    publicationDate: new Date().toISOString(),
    documentTitle: '',
    documentShortCode: '',
    authors: [],
    filters: [],
    description: '',
    fileType: '',
    created: null,
    updatedAt: '',
};

export const emptyAdminSparkContent: IAdminSparkContent = {
    reportType: 'Podcast',
    access: undefined,
    publicationDate: new Date().toISOString(),
    documentTitle: '',
    documentSubtitle: '',
    documentShortCode: '',
    authors: [],
    filters: [],
    description: '',
    bodyHtml: '',
    externalUrl: '',
    spotifyUrl: '',
    linkedinUrl: '',
    linkedinEmbedUrl: '',
    isDraft: false,
    publishAt: '',
    created: null,
    updatedAt: '',
    commentCount: 0,
    routeNames: [],
    siteAreaRelevance: [],
    parentHeader: '',
    thumbnailUrl: undefined,
};

export interface IAdminExcelDocument {
    access?: IAccess;
    oldSubPortalId?: string;
    publicationDate: string;
    documentTitle: string;
    documentShortCode: string;
    authors: Array<string>;
    filters: Array<IDocumentFilter>;
    description?: string;
    fileType: string;
    created?: string | null;
    updatedAt?: string;
}

export interface IAdminPDFDocument {
    access?: IAccess;
    oldSubPortalId?: string;
    publicationDate: string;
    fileName: string;
    documentTitle: string;
    documentShortCode: string;
    authors: Array<string>;
    filters: Array<IDocumentFilter>;
    description?: string;
    publicPages?: IPublicPages;
    created?: string | null;
    updatedAt?: string;
}

export interface IAdminPowerBi {
    access?: IAccess;
    oldSubPortalId?: string;
    publicationDate: string;
    documentTitle: string;
    documentShortCode: string;
    authors: Array<string>;
    filters: Array<IDocumentFilter>;
    description?: string;
    powerBiId: string;
    created?: string | null;
    updatedAt?: string;
}

export interface IAdminSparkContent {
    reportType: SparkContentReportType;
    access?: IAccess;
    oldSubPortalId?: string;
    publicationDate: string;
    documentTitle: string;
    documentSubtitle?: string;
    documentShortCode: string;
    authors: Array<string>;
    filters: Array<IDocumentFilter>;
    description?: string;
    bodyHtml: string;
    externalUrl?: string;
    spotifyUrl?: string;
    linkedinUrl?: string;
    linkedinEmbedUrl?: string;
    isDraft: boolean;
    publishAt?: string;
    created?: string | null;
    updatedAt?: string;
    commentCount: number;
    routeNames: Array<string>;
    siteAreaRelevance: Array<string>;
    parentHeader?: string;
    thumbnailUrl?: string;
    fullImageUrl?: string;
    richTextImages?: Array<IRichTextPendingImage>;
}

export type TAdminDocument = IAdminPDFDocument | IAdminPowerBi | IAdminExcelDocument | IAdminSparkContent;

export interface IBulkDocumentAccessPayload {
    documentShortCodes: Array<string>;
    accessLevelId: string;
    subAccessLevelId?: string | null;
}

export interface IBulkDocumentFilterPayload {
    documentShortCodes: Array<string>;
    filterCategoryId: string;
    filterId: string;
}

export interface IAccess {
    isHidden?: boolean;
    hiddenIfNoAccess?: boolean;
    isPublic?: boolean;
    isTrial?: boolean;
    isObsolete?: boolean;
    access: Array<Array<IContentAccess>>;
    enactRequirement?: IEnactAccessRequirement[];
    enactAccessRequirements?: IEnactAccessRequirement[];
}

export interface IContentAccess {
    product: string | null;
    accessLevel?: {
        access: Array<string>;
        id: string;
    };
}
